/**
 * The plan store: the working plan, edit/run mode, the timer, the shared
 * live session (host/follower) and "Today's Plan". A React port of the
 * plugin's plan/timer/session/posted globals with every rule from
 * live-session-sync.md, stray-stop-broadcast.md, stop-button-guard.md and
 * timer-autostart-and-audio.md kept:
 *
 *  - Start publishes the session; a failed publish is LOUD (LOCAL ONLY).
 *  - Followers re-anchor to server_now - started_at; never the device clock.
 *  - lastKnownSessionStart is committed LAST, inside the try.
 *  - Sessions older than 8 h are ignored; a page load never adopts one.
 *  - Only the host (or a head) stops the shared session; the host self-stops
 *    after 2 consecutive misses; a mode switch / page load never broadcasts.
 *  - Vibration is the primary cue on every device (30 s short, change long);
 *    sound is an optional host-only extra.
 *  - Two-tap Stop.
 *  - Posted plan is a pointer; the banner/badge/dismiss flows carry over.
 */
import {
  autoStartAnchor,
  autoStartKey,
  practiceWindowReached,
  disarmStop,
  emptyPlan,
  fmt,
  initialTimerState,
  kvGet,
  kvSet,
  onStopButtonTap,
  planIsUntouched,
  newId,
  planDataFor,
  planFromShell,
  planSignature,
  shellCopyName,
  shouldAutosave,
  uniquePlanName,
  sessionIsLive,
  startLocal,
  startOffsetMs,
  stopArmExpired,
  stopLocal,
  tick,
  type PlanData,
  type PlanRow,
  type TimerState,
  type TimerView,
} from '@coach/core';
import { useConfirm, useToast } from '@coach/ui';
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp, useAutosaver } from '../../lib/app';
import { armAudio, initAudioArming, isAudioArmed, onAudioArmed, playPeriodChange, playWarning, prepareCueSounds, startSilentLoop, stopSilentLoop } from './audio';
import { buzzLong, buzzShort, buzzTiny, cancelCueNotifications, scheduleCueNotifications } from './haptics';
import { kvGet as kvGetSetting, freezePeriods, scriptLib, describePeriod, getType, type DrillData } from '@coach/core';
import { expiredByCutoff, sessionBackend, type PostedInfo, type SessionInfo } from './backend';

export interface WorkingPlan extends PlanData {
  /** plans.id when loaded from / saved to the library; null for an unsaved draft. */
  _id: string | null;
  _folderId: string | null;
  _canEdit: boolean;
  _owner: string;
}

export type PlanMode = 'edit' | 'run';

export interface SessionUi {
  isHost: boolean;
  published: boolean;
  /** Following someone else's live session. */
  following: boolean;
  /** Short status line under the timer. */
  note: string;
  pollFails: number;
}

export interface PostedUi {
  info: PostedInfo | null;
  banner: { kind: 'posted' | 'updated'; info: PostedInfo } | null;
  isThisPlan: boolean;
}

/** What the Plan page shows next to its buttons. */
export type SaveState =
  | { kind: 'idle' }
  | { kind: 'saving' }
  | { kind: 'saved' }
  | { kind: 'error'; message: string };

export interface PlanStore {
  plan: WorkingPlan;
  setPlan: (updater: (p: WorkingPlan) => WorkingPlan) => void;
  loadRow: (row: PlanRow, opts?: { silent?: boolean }) => void;
  newPlan: () => void;
  mode: PlanMode;
  toggleMode: () => Promise<void>;
  timer: TimerState;
  view: TimerView;
  startFromIdx: number;
  setStartFromIdx: (i: number) => void;
  onStopTap: () => Promise<void>;
  startTimer: (opts?: { anchorMs?: number; auto?: boolean }) => Promise<void>;
  stopTimer: (opts?: { localOnly?: boolean }) => Promise<void>;
  session: SessionUi;
  posted: PostedUi;
  togglePost: () => Promise<void>;
  postPlanId: (id: string, name: string) => Promise<boolean>;
  unpost: () => Promise<void>;
  openPosted: () => Promise<void>;
  dismissBanner: () => void;
  audioArmed: boolean;
  savePlan: (opts?: { force?: boolean }) => Promise<PlanRow | null>;
  /** Plan edits save themselves; this is where that stands. */
  saveState: SaveState;
  /** Save the working plan as a new plan under `name` and switch to it. */
  saveAsNew: (name: string, opts?: { shell?: boolean }) => Promise<PlanRow | null>;
  /** Open a shell the normal way: start a dated copy and load that. */
  startFromShell: (row: PlanRow) => Promise<PlanRow | null>;
  /** Forget the working plan without saving it (it was just trashed). */
  discardCurrent: () => void;
}

const Ctx = createContext<PlanStore | null>(null);
export function usePlanStore(): PlanStore {
  const v = useContext(Ctx);
  if (!v) throw new Error('usePlanStore outside provider');
  return v;
}

/**
 * Synchronous backup of an edit that hasn't reached the database yet. The
 * IndexedDB write is async, and a page being closed or reloaded can die
 * before it lands; localStorage is written synchronously on every change,
 * so the next launch can finish the save. Cleared once the save lands.
 */
const UNSAVED_KEY = 'plan_unsaved_v1';
interface UnsavedStash { id: string | null; plan: PlanData; folderId: string | null; at: number }
function writeStash(v: UnsavedStash | null): void {
  try {
    if (v) localStorage.setItem(UNSAVED_KEY, JSON.stringify(v));
    else localStorage.removeItem(UNSAVED_KEY);
  } catch {
    /* private mode / quota — the async save still runs */
  }
}
function readStash(): UnsavedStash | null {
  try {
    const raw = localStorage.getItem(UNSAVED_KEY);
    return raw ? (JSON.parse(raw) as UnsavedStash) : null;
  } catch {
    return null;
  }
}

function fresh(): WorkingPlan {
  return { ...emptyPlan(), _id: null, _folderId: null, _canEdit: true, _owner: '' };
}

export function rowToWorking(row: PlanRow, canEdit: boolean, owner: string): WorkingPlan {
  const d = row.data ?? emptyPlan(row.name);
  const periods = Array.isArray(d.periods) ? d.periods : [];
  return {
    name: row.name,
    offFocus: d.offFocus || '',
    defFocus: d.defFocus || '',
    startTime: d.startTime || (periods[0]?.time ? fmt(periods[0].time) : '15:00'),
    periods,
    ...(d.shell === true ? { shell: true } : {}),
    _id: row.id,
    _folderId: row.folder_id,
    _canEdit: canEdit,
    _owner: owner,
  };
}

export function LiveSessionProvider({ children }: { children: ReactNode }) {
  const app = useApp();
  const toast = useToast();
  const confirm = useConfirm();
  const navigate = useNavigate();
  const teamId = app.team.id;

  const [plan, setPlanState] = useState<WorkingPlan>(fresh);
  const planRef = useRef(plan);
  planRef.current = plan;
  // ── autosave bookkeeping ────────────────────────────────────────────────
  // genRef changes whenever a DIFFERENT plan becomes the working plan, so a
  // save that finishes late never stamps its id onto the next plan.
  // lastSigRef is the signature of what is known to be in the database for
  // the working plan; autosave runs only when the working plan differs.
  const genRef = useRef(0);
  const lastSigRef = useRef(planSignature(plan));
  const draftIdRef = useRef<{ gen: number; id: string } | null>(null);
  const saveChainRef = useRef<Promise<unknown>>(Promise.resolve());
  const autosaver = useAutosaver(1200);
  const [saveState, setSaveState] = useState<SaveState>({ kind: 'idle' });
  /** Make `next` the working plan. `savedSig` = it is already stored as-is. */
  const replacePlan = useCallback((next: WorkingPlan) => {
    genRef.current++;
    draftIdRef.current = null;
    lastSigRef.current = planSignature(next);
    planRef.current = next;
    setPlanState(next);
    setSaveState({ kind: 'idle' });
  }, []);
  const [mode, setModeState] = useState<PlanMode>('edit');
  const modeRef = useRef(mode);
  modeRef.current = mode;
  const [timer, setTimerState] = useState<TimerState>(initialTimerState);
  const timerRef = useRef(timer);
  timerRef.current = timer;
  const [view, setView] = useState<TimerView>(() => tick(plan, timer, Date.now(), () => '').view);
  const [startFromIdx, setStartFromIdx] = useState(0);
  const [session, setSession] = useState<SessionUi>({ isHost: false, published: false, following: false, note: '', pollFails: 0 });
  const sessionRef = useRef(session);
  const isHeadRef = useRef(app.isHead);
  isHeadRef.current = app.isHead;
  sessionRef.current = session;
  const [postedInfo, setPostedInfo] = useState<PostedInfo | null>(null);
  const postedInfoRef = useRef(postedInfo);
  postedInfoRef.current = postedInfo;

  /** Mark today's auto-start used if a timer is running inside the practice
   *  window. Test runs outside the window leave it armed. */
  const markIfPracticeWindow = useCallback(async () => {
    const midnight = new Date();
    midnight.setHours(0, 0, 0, 0);
    if (practiceWindowReached(timerRef.current, planRef.current, Date.now(), midnight.getTime())) {
      await kvSet(autoStartKey(new Date()), '1');
    }
  }, []);
  const postedRef = useRef<PostedInfo | null>(null); // what this device has ACTED on
  const snoozedRevRef = useRef<string | null>(null);
  const [banner, setBanner] = useState<PostedUi['banner']>(null);
  const [audioArmed, setAudioArmed] = useState(isAudioArmed());
  const soundRef = useRef(false);
  useEffect(() => {
    void kvGetSetting<boolean>('sound_cues').then((v) => { soundRef.current = v === true; if (v === true) prepareCueSounds(); });
    const onChange = (e: Event) => { soundRef.current = !!(e as CustomEvent<boolean>).detail; if (soundRef.current) prepareCueSounds(); };
    window.addEventListener('sound-cues-changed', onChange);
    return () => window.removeEventListener('sound-cues-changed', onChange);
  }, []);
  const lastKnownSessionStart = useRef<number | null>(null);
  const hostMisses = useRef(0);
  const pollSeq = useRef(0);
  const pollSeen = useRef(0);
  const anchorMsRef = useRef<number | null>(null);

  const setPlan = useCallback((u: (p: WorkingPlan) => WorkingPlan) => setPlanState((p) => u(p)), []);
  const setTimer = useCallback((u: (t: TimerState) => TimerState) => {
    const next = u(timerRef.current);
    timerRef.current = next;
    setTimerState(next);
  }, []);

  useEffect(() => {
    initAudioArming();
    return onAudioArmed(setAudioArmed);
  }, []);

  // ── period name lookup for the timer banner ────────────────────────────
  const drillNames = useRef(new Map<string, string>());
  useEffect(() => {
    let alive = true;
    void app.repos.library.listLive().then((items) => {
      if (!alive) return;
      const m = new Map<string, string>();
      for (const it of items) if (it.kind === 'drill') m.set(it.id, (it.data as { name?: string }).name ?? '');
      drillNames.current = m;
    });
    return () => {
      alive = false;
    };
  }, [app.repos.library, plan]);
  // Lock-screen notification wording for period i of `periods` (the live plan,
  // or a followed session's snapshot): activity names, group chips, type label.
  // Read through a ref: the start/poll callbacks that use this aren't rebuilt
  // when the team's period types change.
  const typeSettings = useRef({ custom: app.settings.period_types ?? [], hidden: app.settings.hidden_builtins ?? [] });
  typeSettings.current = { custom: app.settings.period_types ?? [], hidden: app.settings.hidden_builtins ?? [] };
  const describeIn = useCallback(
    (periods: PlanData['periods']) => (i: number) => {
      const p = periods[i]!;
      const label = getType(p.type, typeSettings.current.custom, typeSettings.current.hidden).label;
      return describePeriod(p, label, (id) => drillNames.current.get(id));
    },
    [],
  );
  const periodName = useCallback(
    (idx: number) => {
      const p = planRef.current.periods[idx];
      if (!p) return 'Practice';
      const a = p.assignments[0];
      if (a?.customActivity) return a.customActivity;
      if (a?.drillId) return drillNames.current.get(a.drillId) || p.type;
      return p.type;
    },
    [],
  );

  // ── 1 s tick ────────────────────────────────────────────────────────────
  useEffect(() => {
    let lastRun = 0;
    const run = () => {
      const t = timerRef.current;
      const now = Date.now();
      // Cues only fire live, on screen. In the background iOS keeps this
      // interval ticking for a while and holds every vibration it asks for,
      // then plays the whole backlog on return — and the booked notifications
      // already covered those changes. A long gap since the last tick means
      // we're catching up after a suspend: take the cues (so they're marked
      // done) but don't play them.
      const live = document.visibilityState === 'visible' && (lastRun === 0 || now - lastRun < 5000);
      lastRun = now;
      if (t.stopArmed && stopArmExpired(t, now)) setTimer(disarmStop);
      const r = tick(planRef.current, timerRef.current, now, periodName);
      if (r.state !== timerRef.current) setTimer(() => r.state);
      setView(r.view);
      if (!live) return;
      for (const cue of r.view.cues) {
        // Vibration is the primary cue on every device: short at 30 s, long at the change.
        if (cue === 'thirty') void buzzShort();
        if (cue === 'periodChange') void buzzLong();
        // Sound is an optional extra, host only (the PWA-era workaround).
        if (timerRef.current.isHost && soundRef.current) {
          if (cue === 'periodChange') playPeriodChange();
          if (cue === 'warning') playWarning();
        }
      }
    };
    run();
    const iv = setInterval(run, 1000);
    // Coming back to the app: tick straight away (silently — the gap check
    // above sees the pause) so the screen is right before the next second.
    const onVis = () => { if (document.visibilityState === 'visible') run(); };
    document.addEventListener('visibilitychange', onVis);
    return () => { clearInterval(iv); document.removeEventListener('visibilitychange', onVis); };
  }, [periodName, setTimer]);

  // keep the screen awake while running (native)
  useEffect(() => {
    let cancelled = false;
    void import('@capacitor-community/keep-awake')
      .then(({ KeepAwake }) => (timer.running && !cancelled ? KeepAwake.keepAwake() : KeepAwake.allowSleep()))
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [timer.running]);

  // ── stop / start ────────────────────────────────────────────────────────
  const stopTimer = useCallback(
    async (opts?: { localOnly?: boolean }) => {
      const wasRunning = timerRef.current.running;
      const wasHost = timerRef.current.isHost;
      // The server lets the host or a head coach end the practice, so a head
      // following someone else's session can stop it for everyone too.
      const headFollowing = sessionRef.current.following && isHeadRef.current;
      // Stopping inside the window must not let auto-start fire straight back.
      try {
        await markIfPracticeWindow();
      } catch {
        /* best effort */
      }
      setTimer((t) => stopLocal(disarmStop(t)));
      stopSilentLoop();
      void cancelCueNotifications();
      anchorMsRef.current = null;
      setStartFromIdx(0);
      const broadcast = wasRunning && (wasHost || headFollowing) && !opts?.localOnly;
      let stoppedOnServer = false;
      if (broadcast) {
        try {
          stoppedOnServer = await sessionBackend.stop(teamId);
        } catch {
          /* reported below */
        }
        if (!stoppedOnServer) toast('⚠ Your timer stopped, but the server still shows practice as live. Other devices may keep running.', 6000);
      }
      if (stoppedOnServer) lastKnownSessionStart.current = null;
      hostMisses.current = 0;
      setSession((s) => ({ ...s, isHost: false, published: false, following: false, note: '' }));
    },
    [teamId, toast, setTimer, markIfPracticeWindow],
  );

  const startTimer = useCallback(
    async (opts?: { anchorMs?: number; auto?: boolean }) => {
      let p = planRef.current;
      if (!p.periods.length) {
        toast('Add periods first');
        return;
      }
      if (timerRef.current.running) return;
      // An auto-start uses up the day. A manual start only does once it is
      // running inside the practice window (markIfPracticeWindow, on each
      // poll and on stop) — so testing the timer earlier doesn't disarm it.
      if (opts?.auto) await kvSet(autoStartKey(new Date()), '1');
      // Claim host BEFORE the await so a poll tick can't treat us as a follower.
      setTimer((t) => ({ ...t, isHost: true }));
      if (soundRef.current) startSilentLoop();
      // Freeze every play script at the wording it is being run with, so a
      // call renamed later never rewrites this practice. Only on a plan this
      // coach can save, never on a shell (a shell is a template, not a practice).
      if (p._canEdit && !p.shell) {
        try {
          const [live, gone] = await Promise.all([app.repos.library.listLive(), app.repos.library.listTrash()]);
          const drillById = new Map(live.filter((r) => r.kind === 'drill').map((r) => [r.id, r.data as DrillData]));
          const f = freezePeriods(p.periods, (id) => drillById.get(id), scriptLib([...live, ...gone]));
          if (f.changed && planRef.current === p) {
            p = { ...p, periods: f.periods };
            planRef.current = p;
            setPlanState(p);
          }
        } catch (e) {
          console.warn('[plan] could not freeze scripts', e);
        }
      }
      const now = Date.now();
      const offsetMs = startOffsetMs(p, { startFromIdx, anchorMs: opts?.anchorMs, nowMs: now });
      const localAnchor = now - offsetMs;
      let published = false,
        failWhy = '';
      try {
        const r = await sessionBackend.start(teamId, { name: p.name || 'Practice', offFocus: p.offFocus, defFocus: p.defFocus, startTime: p.startTime, periods: p.periods }, offsetMs);
        lastKnownSessionStart.current = r.startedAtMs;
        published = true;
      } catch (e) {
        failWhy = (e as Error).message || 'network error';
      }
      anchorMsRef.current = localAnchor;
      setTimer((t) => startLocal(t, localAnchor, true, published));
      void scheduleCueNotifications(p, localAnchor, describeIn(p.periods));
      hostMisses.current = 0;
      setSession({ isHost: true, published, following: false, note: published ? '🔴 Live · shared with other devices' : '⚠ LOCAL ONLY — not shared', pollFails: 0 });
      if (!published) toast(`⚠ Couldn't share this practice (${failWhy}). Your timer is running, but other devices will NOT see it.`, 6500);
      if (opts?.auto) {
        const late = Math.round(offsetMs / 60000);
        toast(`⏱ Practice started automatically at ${fmt(p.periods[0]!.time)}${late >= 1 ? ` · caught up ${late} min` : ''}. Tap Stop to take over.`, 6500);
      }
    },
    [teamId, toast, startFromIdx, setTimer, app.repos.library],
  );

  const onStopTap = useCallback(async () => {
    if (sessionRef.current.following && !isHeadRef.current) return; // assistants following can't stop
    const r = onStopButtonTap(timerRef.current, Date.now());
    setTimer(() => r.state);
    if (r.result.action === 'start') await startTimer();
    else if (r.result.action === 'armed') {
      void buzzTiny();
      toast('Tap again to end practice', 4000);
    } else await stopTimer();
  }, [startTimer, stopTimer, toast, setTimer]);

  const toggleMode = useCallback(async () => {
    if (modeRef.current === 'run' && timerRef.current.running) {
      const shared = (timerRef.current.isHost && timerRef.current.published) || (sessionRef.current.following && isHeadRef.current);
      const ok = await confirm(
        shared ? 'End the live practice and go back to Edit Mode?\n\nEvery coach synced to this practice will stop too.' : 'Stop the timer and go back to Edit Mode?',
        { ok: 'End practice', danger: true },
      );
      if (!ok) return;
      await stopTimer();
    }
    const next: PlanMode = modeRef.current === 'edit' ? 'run' : 'edit';
    modeRef.current = next;
    setModeState(next);
  }, [confirm, stopTimer]);

  // ── posted plan ─────────────────────────────────────────────────────────
  const loadPostedPlan = useCallback(
    async (p: PostedInfo, silent: boolean): Promise<boolean> => {
      const row = await app.repos.plans.get(p.plan_id);
      if (!row || row.deleted_at) {
        setSession((s) => ({ ...s, note: '⚠ Could not load Today’s Plan — retrying' }));
        return false;
      }
      if (!row.data?.periods?.length) {
        setSession((s) => ({ ...s, note: '⚠ Today’s Plan has no periods yet' }));
        return false;
      }
      const canEdit = app.isHead || row.owner_id === app.userId;
      void autosaver.flush(); // the plan being left keeps its last edits
      replacePlan(rowToWorking(row, canEdit, ''));
      // A failed first attempt (the row not pulled yet on a fresh device)
      // left a warning behind; the retry worked, so take it down.
      setSession((s) => (s.note.includes('Today’s Plan') ? { ...s, note: '' } : s));
      if (!app.isHead && modeRef.current !== 'run') {
        modeRef.current = 'run';
        setModeState('run');
      }
      setPostedInfo(p);
      postedRef.current = p;
      snoozedRevRef.current = null;
      setBanner(null);
      if (!silent) toast('📌 Loaded Today’s Plan');
      return true;
    },
    [app.repos.plans, app.isHead, app.userId, toast, autosaver, replacePlan],
  );

  const applyPostedState = useCallback(
    async (raw: PostedInfo | null, nowMs: number) => {
      const p = raw && !expiredByCutoff(raw.posted_at_ms, nowMs, app.settings.practice_day_cutoff_hour ?? 3) ? raw : null;
      setPostedInfo(p);
      const prev = postedRef.current;
      const same = (!p && !prev) || (p && prev && p.plan_id === prev.plan_id && p.rev === prev.rev);
      if (same) return;
      if (!p) {
        postedRef.current = null;
        snoozedRevRef.current = null;
        setBanner(null);
        if (prev) toast('Today’s Plan was taken down');
        return;
      }
      if (timerRef.current.running) return; // a live session outranks the posted plan
      const cur = planRef.current;
      const viewingIt = cur._id === p.plan_id;
      if (viewingIt) {
        // The rev moves on every save of the posted plan — including our own
        // autosaves. Reloading on those would toast at the author and could
        // throw away keystrokes typed since. Our own save: just note the rev.
        // Unsaved edits here: wait; they save shortly and the next poll sees it.
        const row = await app.repos.plans.get(p.plan_id);
        const dirty = planSignature(planRef.current) !== lastSigRef.current;
        if (dirty && planRef.current._canEdit) return;
        if (row && planSignature(row.data) === lastSigRef.current) {
          postedRef.current = p;
          return;
        }
      }
      if (viewingIt || planIsUntouched(cur, !!cur._id)) {
        const ok = await loadPostedPlan(p, viewingIt && !!prev);
        if (ok && viewingIt && prev) toast('📌 Today’s Plan updated');
      } else if (snoozedRevRef.current === p.rev) {
        postedRef.current = p;
      } else {
        postedRef.current = p;
        setBanner({ kind: prev && prev.plan_id === p.plan_id ? 'updated' : 'posted', info: p });
      }
    },
    [app.settings.practice_day_cutoff_hour, app.repos.plans, loadPostedPlan, toast],
  );

  /**
   * Save one snapshot of the working plan. Saves run one at a time (a chain),
   * so a new draft's second autosave finds the row the first one created
   * instead of inserting a duplicate (the v2.31.0 duplicate-plays bug).
   *
   * auto: an autosave — never asks anything, never overwrites a different
   * plan, never resurrects a trashed one, never copies someone else's plan.
   */
  const doSave = useCallback(
    (p: WorkingPlan, gen: number, opts: { auto?: boolean; force?: boolean } = {}): Promise<PlanRow | null> => {
      const job = async (): Promise<PlanRow | null> => {
        let name = p.name.trim();
        if (!name) {
          if (opts.auto) {
            setSaveState({ kind: 'error', message: 'Give the plan a name to save it' });
            return null;
          }
          toast('Give the plan a name first');
          return null;
        }
        const live = await app.repos.plans.listLive();
        const id = p._id ?? (draftIdRef.current?.gen === gen ? draftIdRef.current.id : null);
        let row: PlanRow | undefined = id ? await app.repos.plans.get(id) : undefined;
        if (row && row.deleted_at) {
          if (opts.auto) return null; // trashed elsewhere — don't bring it back
          row = undefined;
        }
        // Someone else's plan → save a copy (ownership rule).
        if (row && !(app.isHead || row.owner_id === app.userId)) {
          if (opts.auto) return null;
          if (!opts.force) {
            const ok = await confirm(`"${row.name}" belongs to ${p._owner || 'another coach'}. Save your own copy?`, { ok: 'Save copy' });
            if (!ok) return null;
          }
          row = undefined;
        }
        const clash = live.find((r) => r.name.toLowerCase() === name.toLowerCase() && r.id !== row?.id);
        if (clash) {
          if (opts.auto) {
            if (row) {
              setSaveState({ kind: 'error', message: `Not saved — another plan is already named “${name}”. Tap the name to rename this one.` });
              return null;
            }
            name = uniquePlanName(name, live.map((r) => r.name)); // a brand-new draft just gets a free name
          } else if (app.isHead || clash.owner_id === app.userId) {
            if (!opts.force) {
              const ok = await confirm(`A plan named "${name}" already exists. Overwrite it?`, { ok: 'Overwrite', danger: true });
              if (!ok) return null;
            }
            row = clash;
          } else {
            toast(`"${name}" is another coach's plan — pick a different name`);
            return null;
          }
        }
        const data = planDataFor(p, name);
        // A new draft keeps the id minted when its first autosave was scheduled
        // (it is also in the unsaved-edit backup), so a retry is an update.
        const blank = app.repos.plans.blank({ name, folder_id: p._folderId, data });
        const next = row ? { ...row, name, data } : !p._id && id ? { ...blank, id } : blank;
        if (!row && !p._id) draftIdRef.current = { gen, id: next.id };
        const saved = await app.repos.plans.save(next);
        if (genRef.current === gen) {
          const cur = planRef.current;
          const adopted: WorkingPlan = { ...cur, name: cur.name === p.name ? name : cur.name, _id: saved.id, _folderId: saved.folder_id, _canEdit: true };
          planRef.current = adopted;
          setPlanState(adopted);
          lastSigRef.current = planSignature(data);
          const st = readStash();
          if (st && planSignature(st.plan) === lastSigRef.current) writeStash(null);
          // later edits made while this save ran are picked up by the effect
          setSaveState(planSignature(adopted) === lastSigRef.current ? { kind: 'saved' } : { kind: 'saving' });
        }
        return saved;
      };
      const run = saveChainRef.current.then(job, job);
      saveChainRef.current = run.catch(() => null);
      return run.catch((e: unknown) => {
        console.error('[plan] save failed:', e);
        if (genRef.current === gen) setSaveState({ kind: 'error', message: 'Could not save: ' + ((e as Error).message || 'unknown error') });
        return null;
      });
    },
    [app.repos.plans, app.isHead, app.userId, confirm, toast],
  );

  // Autosave: every real change to an editable plan, 1.2 s after the last
  // one, flushed on pagehide / hidden / app backgrounded (useAutosaver).
  useEffect(() => {
    const sig = planSignature(plan);
    const go = shouldAutosave({
      signature: sig,
      lastSavedSignature: lastSigRef.current,
      canEdit: plan._canEdit,
      following: sessionRef.current.following,
      untouchedDraft: !plan._id && planIsUntouched(plan, false),
    });
    if (!go) return;
    const gen = genRef.current;
    if (!plan._id && draftIdRef.current?.gen !== gen) draftIdRef.current = { gen, id: newId() };
    const id = plan._id ?? draftIdRef.current!.id;
    writeStash({ id, plan: planDataFor(plan), folderId: plan._folderId, at: Date.now() });
    setSaveState({ kind: 'saving' });
    autosaver.schedule(() => doSave(plan, gen, { auto: true }).then(() => undefined));
  }, [plan, autosaver, doSave]);

  /** Explicit save (posting uses it): flush any pending autosave, then save. */
  const savePlan = useCallback(
    async (opts?: { force?: boolean }): Promise<PlanRow | null> => {
      await autosaver.flush();
      const saved = await doSave(planRef.current, genRef.current, { force: opts?.force });
      if (saved && !opts?.force) toast('✓ Saved');
      return saved;
    },
    [autosaver, doSave, toast],
  );

  const saveAsNew = useCallback(
    async (want: string, opts?: { shell?: boolean }): Promise<PlanRow | null> => {
      await autosaver.flush();
      await saveChainRef.current;
      const name = want.trim();
      if (!name) return null;
      const live = await app.repos.plans.listLive();
      if (live.some((r) => r.name.toLowerCase() === name.toLowerCase())) {
        toast(`A plan named “${name}” already exists — pick another name`);
        return null;
      }
      const cur = planRef.current;
      const data = planDataFor({ ...cur, shell: opts?.shell === true }, name);
      const saved = await app.repos.plans.save(app.repos.plans.blank({ name, folder_id: cur._folderId, data }));
      replacePlan(rowToWorking(saved, true, ''));
      setSaveState({ kind: 'saved' });
      toast(opts?.shell ? `✓ Saved as shell “${name}”` : `✓ Saved as “${name}”`);
      return saved;
    },
    [app.repos.plans, autosaver, replacePlan, toast],
  );

  const postPlanId = useCallback(
    async (id: string, name: string) => {
      try {
        const p = await (sessionBackend as { post: (t: string, id: string | null, extra?: { name: string }) => Promise<PostedInfo | null> }).post(teamId, id, { name });
        setPostedInfo(p);
        postedRef.current = p;
        snoozedRevRef.current = null;
        setBanner(null);
        toast('📌 Posted as Today’s Plan');
        return true;
      } catch (e) {
        toast((e as Error).message === 'not_saved' ? 'Save the plan first, then post it' : 'Could not post: ' + (e as Error).message);
        return false;
      }
    },
    [teamId, toast],
  );
  const unpost = useCallback(async () => {
    try {
      await sessionBackend.post(teamId, null);
      setPostedInfo(null);
      postedRef.current = null;
      snoozedRevRef.current = null;
      setBanner(null);
      toast('Today’s Plan taken down');
    } catch (e) {
      toast('Could not take it down: ' + (e as Error).message);
    }
  }, [teamId, toast]);

  const togglePost = useCallback(async () => {
    if (!app.isHead) return;
    const p = planRef.current;
    if (!p.name) {
      toast('Nothing to post');
      return;
    }
    if (postedInfoRef.current && postedInfoRef.current.plan_id === p._id) {
      const ok = await confirm(`Take "${p.name}" down as Today’s Plan?\n\nYour assistants will stop seeing it.`, { ok: 'Take down', danger: true });
      if (ok) await unpost();
      return;
    }
    let id = p._id;
    if (!id) {
      const saved = await savePlan();
      if (!saved) return;
      id = saved.id;
    } else {
      // make sure the latest edits are what gets posted
      const saved = await savePlan({ force: true });
      if (!saved) return;
      id = saved.id;
    }
    await app.sync(); // the pointer must resolve server-side before assistants poll
    await postPlanId(id, p.name);
  }, [app, confirm, postPlanId, savePlan, toast, unpost]);

  const openPosted = useCallback(async () => {
    const p = postedInfoRef.current;
    if (!p) {
      toast('No plan is posted right now');
      return;
    }
    navigate('/plan');
    await loadPostedPlan(p, false);
  }, [loadPostedPlan, navigate, toast]);

  const dismissBanner = useCallback(() => {
    if (postedInfoRef.current) snoozedRevRef.current = postedInfoRef.current.rev;
    setBanner(null);
  }, []);

  // ── poll (3 s) ──────────────────────────────────────────────────────────
  const poll = useCallback(async () => {
    const ticket = ++pollSeq.current;
    try {
      await markIfPracticeWindow(); // before the fetch: counts even when offline
    } catch (err) {
      console.error('[plan] auto-start mark failed:', err);
    }
    let st;
    try {
      st = await sessionBackend.getState(teamId);
    } catch (e) {
      setSession((s) => ({ ...s, pollFails: s.pollFails + 1, note: s.pollFails + 1 >= 3 ? `⚠ Sync offline (${(e as Error).message})` : s.note }));
      return;
    }
    if (ticket < pollSeen.current) return; // overtaken by a newer poll
    pollSeen.current = ticket;
    setSession((s) => (s.pollFails ? { ...s, pollFails: 0, note: s.note.startsWith('⚠ Sync') ? '' : s.note } : s));

    try {
      await applyPostedState(st.posted, st.serverNowMs);
    } catch (err) {
      console.error('[plan] posted-plan sync failed:', err);
    }

    // auto-start (head only)
    try {
      const done = (await kvGet<string>(autoStartKey(new Date()))) === '1';
      const midnight = new Date();
      midnight.setHours(0, 0, 0, 0);
      const anchor = autoStartAnchor({
        isHead: app.isHead,
        timer: timerRef.current,
        sessionLive: sessionIsLive(st.session?.started_at_ms ?? null, st.serverNowMs),
        postedPlanId: postedInfoRef.current?.plan_id ?? null,
        currentPlanId: planRef.current._id,
        autoStartDoneToday: done,
        plan: planRef.current,
        nowMs: Date.now(),
        todayMidnightMs: midnight.getTime(),
      });
      if (anchor !== null) {
        if (modeRef.current !== 'run') {
          modeRef.current = 'run';
          setModeState('run');
        }
        await startTimer({ anchorMs: anchor, auto: true });
        return;
      }
    } catch (err) {
      console.error('[plan] auto-start check failed:', err);
    }

    const t = timerRef.current;
    if (t.isHost) {
      if (!t.running || !t.published) return;
      if (!st.session) {
        if (++hostMisses.current >= 2) {
          hostMisses.current = 0;
          toast('Practice session was ended on another device');
          await stopTimer({ localOnly: true });
        }
      } else hostMisses.current = 0;
      return;
    }

    const sess: SessionInfo | null = st.session;
    if (!sess) {
      if (t.running) {
        setTimer((x) => stopLocal(disarmStop(x)));
        void cancelCueNotifications();
        anchorMsRef.current = null;
        setSession((s) => ({ ...s, following: false, note: '' }));
        toast('Practice session ended');
      }
      lastKnownSessionStart.current = null;
      return;
    }
    const incoming = sess.started_at_ms;
    if (!sessionIsLive(incoming, st.serverNowMs)) {
      if (lastKnownSessionStart.current !== incoming) {
        lastKnownSessionStart.current = incoming;
        console.warn('[plan] ignoring stale live session from', new Date(incoming).toString());
      }
      return;
    }
    const elapsedMs = st.serverNowMs - incoming;
    const anchorMs = Date.now() - elapsedMs;
    if (incoming !== lastKnownSessionStart.current) {
      try {
        const d = sess.plan_snapshot || ({} as PlanData);
        const periods = Array.isArray(d.periods) ? d.periods : [];
        if (!periods.length) throw new Error('shared plan had no periods');
        void autosaver.flush();
        genRef.current++;
        setPlanState((cur) => ({
          ...cur,
          name: d.name || 'Practice',
          offFocus: d.offFocus || '',
          defFocus: d.defFocus || '',
          startTime: d.startTime || fmt(periods[0]!.time) || '15:00',
          periods,
          _canEdit: false,
          _owner: '',
        }));
        if (modeRef.current !== 'run') {
          modeRef.current = 'run';
          setModeState('run');
        }
        anchorMsRef.current = anchorMs;
        // A practice this account started is ours again — after iOS reloads
        // the app in the background, or on the same coach's other device:
        // Stop, sound and host duties come back instead of a read-only
        // follower view. Stale (8 h) sessions never get this far.
        const mine = !!app.userId && sess.host_user_id === app.userId;
        if (mine) {
          setTimer((x) => (x.running ? { ...x, anchorMs, isHost: true, published: true } : startLocal(x, anchorMs, true, true)));
          void scheduleCueNotifications({ name: d.name || 'Practice', offFocus: '', defFocus: '', startTime: d.startTime || '', periods }, anchorMs, describeIn(periods));
          if (soundRef.current) startSilentLoop();
          hostMisses.current = 0;
          lastKnownSessionStart.current = incoming;
          setSession((s) => ({ ...s, following: false, isHost: true, published: true, note: '🔴 Live · shared with other devices' }));
          toast('🔴 Back on your live practice');
          return;
        }
        if (!timerRef.current.running) {
          setTimer((x) => startLocal(x, anchorMs, false, true));
          void scheduleCueNotifications({ name: d.name || 'Practice', offFocus: '', defFocus: '', startTime: d.startTime || '', periods }, anchorMs, describeIn(periods));
          toast('🔴 Synced to live practice');
        } else {
          setTimer((x) => ({ ...x, anchorMs }));
        }
        lastKnownSessionStart.current = incoming; // committed only now that it landed
        setSession((s) => ({ ...s, following: true, isHost: false, published: false, note: '🔴 Live · synced' }));
      } catch (err) {
        console.error('[plan] live sync failed:', err);
        setSession((s) => ({ ...s, note: '⚠ Sync error — retrying' }));
      }
      return;
    }
    if (timerRef.current.running && app.userId && sess.host_user_id === app.userId && sessionRef.current.following) {
      // Already following our own practice (joined before this fix): take it back.
      setTimer((x) => ({ ...x, anchorMs, isHost: true, published: true }));
      if (soundRef.current) startSilentLoop();
      hostMisses.current = 0;
      setSession((s) => ({ ...s, following: false, isHost: true, published: true, note: '🔴 Live · shared with other devices' }));
      return;
    }
    if (timerRef.current.running) setTimer((x) => ({ ...x, anchorMs })); // drift correction
  }, [teamId, app.isHead, app.userId, applyPostedState, startTimer, stopTimer, toast, setTimer, markIfPracticeWindow, autosaver]);

  useEffect(() => {
    void poll();
    const iv = setInterval(() => void poll(), 3000);
    return () => clearInterval(iv);
  }, [poll]);

  // ── loading / new ───────────────────────────────────────────────────────
  const loadRow = useCallback(
    (row: PlanRow) => {
      const canEdit = app.isHead || row.owner_id === app.userId;
      void autosaver.flush(); // the plan being left keeps its last edits
      replacePlan(rowToWorking(row, canEdit, ''));
      if (!app.isHead && modeRef.current !== 'run' && !timerRef.current.running) {
        modeRef.current = 'run';
        setModeState('run');
      }
    },
    [app.isHead, app.userId, autosaver, replacePlan],
  );
  const newPlan = useCallback(() => {
    void autosaver.flush();
    replacePlan(fresh());
    if (modeRef.current !== 'edit' && !timerRef.current.running) {
      modeRef.current = 'edit';
      setModeState('edit');
    }
  }, [autosaver, replacePlan]);

  const discardCurrent = useCallback(() => {
    autosaver.cancel();
    writeStash(null);
    replacePlan(fresh());
  }, [autosaver, replacePlan]);

  // Finish a save the last session couldn't (the page closed mid-save).
  useEffect(() => {
    const st = readStash();
    if (!st) return;
    void (async () => {
      try {
        const row = st.id ? await app.repos.plans.get(st.id) : undefined;
        if (row && row.deleted_at) return writeStash(null); // trashed since — respect that
        if (row && !(app.isHead || row.owner_id === app.userId)) return writeStash(null);
        if (row && planSignature(row.data) === planSignature(st.plan)) return writeStash(null); // it did land
        if (row && Date.parse(row.updated_at) > st.at) return writeStash(null); // newer edit elsewhere wins
        let target: PlanRow;
        if (row) target = { ...row, name: st.plan.name, data: st.plan };
        else {
          const live = await app.repos.plans.listLive();
          const name = uniquePlanName(st.plan.name, live.map((r) => r.name));
          target = app.repos.plans.blank({ name, folder_id: st.folderId, data: { ...st.plan, name } });
          if (st.id) target = { ...target, id: st.id };
        }
        const saved = await app.repos.plans.save(target);
        writeStash(null);
        if (planIsUntouched(planRef.current, !!planRef.current._id)) replacePlan(rowToWorking(saved, true, ''));
        toast(`Recovered your last edits to “${saved.name}”`);
      } catch (e) {
        console.error('[plan] recovering unsaved edits failed:', e);
      }
    })();
    // once per launch
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startFromShell = useCallback(
    async (shellRow: PlanRow): Promise<PlanRow | null> => {
      const live = await app.repos.plans.listLive();
      const name = shellCopyName(shellRow.name, new Date(), live.map((r) => r.name));
      const copy = await app.repos.plans.save(app.repos.plans.blank({ name, folder_id: shellRow.folder_id, data: planFromShell(shellRow.data, name) }));
      loadRow(copy);
      toast(`Started “${name}” from the ${shellRow.name} shell`);
      return copy;
    },
    [app.repos.plans, loadRow, toast],
  );

  const value = useMemo<PlanStore>(
    () => ({
      plan,
      setPlan,
      loadRow,
      newPlan,
      mode,
      toggleMode,
      timer,
      view,
      startFromIdx,
      setStartFromIdx,
      onStopTap,
      startTimer,
      stopTimer,
      session,
      posted: { info: postedInfo, banner, isThisPlan: !!(postedInfo && plan._id && postedInfo.plan_id === plan._id) },
      togglePost,
      postPlanId,
      unpost,
      openPosted,
      dismissBanner,
      audioArmed,
      savePlan,
      saveState,
      saveAsNew,
      startFromShell,
      discardCurrent,
    }),
    [plan, setPlan, loadRow, newPlan, mode, toggleMode, timer, view, startFromIdx, onStopTap, startTimer, stopTimer, session, postedInfo, banner, togglePost, postPlanId, unpost, openPosted, dismissBanner, audioArmed, savePlan, saveState, saveAsNew, startFromShell, discardCurrent],
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function LiveBadge() {
  const { timer, session } = usePlanStore();
  if (!timer.running) return null;
  const shared = session.following || timer.published;
  return <span className={'live-badge' + (shared ? '' : ' local')}>{shared ? '🔴 LIVE' : '⚠ LOCAL ONLY'}</span>;
}

export { armAudio };
